import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NodeData } from '@/types/workflow';
import { 
  Zap, 
  Send, 
  GitBranch, 
  Brain, 
  Clock, 
  Filter, 
  Shuffle,
  Webhook,
  Calendar,
  User,
  Mail,
  Database,
  MessageSquare,
  BarChart,
  Copy,
  Trash2
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const iconComponents: Record<string, any> = {
  Webhook,
  Calendar,
  User,
  Mail,
  Send,
  Database,
  MessageSquare,
  Brain,
  BarChart,
  GitBranch,
  Filter,
  Clock,
  Shuffle,
  Zap,
};

interface BaseNodeProps extends NodeProps {
  data: NodeData;
}

export const BaseNode = memo(({ data, selected, id }: BaseNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const Icon = data.icon ? iconComponents[data.icon] : Zap;
  const nodeColor = data.color || '#6b7280';
  
  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
      // toast({
      //   title: "Node duplicated",
      //   description: "The node has been duplicated successfully",
      // });
    }
  }, [id, getNodes, setNodes]);


  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
    
    // toast({
    //   title: "Node deleted",
    //   description: "The node and its connections have been removed",
    // });
  }, [id, getNodes, getEdges, setNodes, setEdges]);
  
  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Gumloop-style Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[280px] backdrop-blur-md bg-background/75 transition-all ${
          selected ? 'ring-2 ring-primary shadow-lg' : 'shadow-md hover:shadow-lg'
        }`}
        style={{ 
          borderColor: selected ? nodeColor + '50' : nodeColor + '30',
          boxShadow: selected ? `0 0 20px ${nodeColor}30` : undefined
        }}
      >
        <div className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: nodeColor + '20' }}
            >
              <Icon className="w-4 h-4" style={{ color: nodeColor }} />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label}</div>
              {data.description && (
                <div className="text-xs text-muted-foreground">{data.description}</div>
              )}
            </div>
          </div>
        
        {/* Node-specific content can be rendered here */}
        {data.type === 'condition' && data.config?.conditions?.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            {data.config.conditions.length} condition(s)
          </div>
        )}
        
        {data.type === 'delay' && data.config?.duration && (
          <div className="mt-2 text-xs text-muted-foreground">
            Wait {data.config.duration} {data.config.unit || 'seconds'}
          </div>
        )}
        </div>
      </Card>
      
      {/* Default input handle for most nodes */}
      {data.type !== 'input' && data.type !== 'webhook' && data.type !== 'schedule' && (
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
        />
      )}
      
      {/* Default output handle for most nodes */}
      {data.type !== 'output' && (
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className="custom-handle"
          style={{
            backgroundColor: nodeColor,
            boxShadow: `0 2px 8px ${nodeColor}30`,
          }}
        />
      )}
      
      {/* Conditional outputs for condition nodes */}
      {data.type === 'condition' && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className="custom-handle"
            style={{
              backgroundColor: '#22c55e',
              boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
              top: '35%',
            }}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            className="custom-handle"
            style={{
              backgroundColor: '#ef4444',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              top: '65%',
            }}
          />
        </>
      )}
    </div>
  );
});

BaseNode.displayName = 'BaseNode';
